import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Duration } from 'luxon';
import { useToggleState } from 'app/lib/hooks';
import { Button, ButtonVariant, ButtonEdge, ButtonSize } from 'app/components/core/Button';
import { Dialog, ModalBody, ModalHeader, ModalTitle } from 'app/components/core/Modal';
import {
    Snackbar,
    SnackbarMessage,
    SnackbarVariant,
    SnackbarAction
} from 'app/components/core/Snackbar';

const convertToSeconds = (value) => value * 0.001;

export function TimeoutNotification({ id, onDismiss, payload, i18nKey = null }) {
    const { t } = useTranslation();
    const [isDialogOpen, toggleDialog] = useToggleState(false);

    const timeoutInMillis = payload.data?.timeoutAt
        ? Duration.fromISO(payload.data.timeoutAt).toMillis()
        : null;
    const timeoutInSeconds = convertToSeconds(timeoutInMillis);

    const stackTraceAsString = payload.data?.stackTraceAsString;

    const generalMessage = useMemo(
        () =>
            i18nKey
                ? t(i18nKey, { time: timeoutInSeconds })
                : t('notifications:QueryTimeout.general'),
        [t, i18nKey, timeoutInSeconds]
    );
    const handleDialogClose = useCallback(() => {
        toggleDialog();
        onDismiss(id);
    }, [id, toggleDialog, onDismiss]);

    return (
        <Snackbar id={id} onDismiss={onDismiss} variant={SnackbarVariant.WARNING}>
            <SnackbarMessage>{generalMessage}</SnackbarMessage>
            <SnackbarAction>
                <Button
                    onClick={toggleDialog}
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.GHOST}
                    edge={ButtonEdge.FLAT}>
                    Details
                </Button>
            </SnackbarAction>
            {isDialogOpen && (
                <Dialog onClose={handleDialogClose}>
                    <ModalHeader>
                        <ModalTitle>{generalMessage}</ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                        {timeoutInMillis && (
                            <p>
                                {t('notifications:QueryTimeout.timeoutAt', {
                                    time: timeoutInSeconds
                                })}
                            </p>
                        )}
                        {stackTraceAsString && <code>{stackTraceAsString}</code>}
                    </ModalBody>
                </Dialog>
            )}
        </Snackbar>
    );
}

TimeoutNotification.propTypes = {
    id: PropTypes.string.isRequired,
    onDismiss: PropTypes.func.isRequired,
    payload: PropTypes.shape({
        code: PropTypes.number,
        data: PropTypes.shape({
            timeoutAt: PropTypes.string,
            message: PropTypes.string,
            stackTraceAsString: PropTypes.string
        })
    }).isRequired,
    i18nKey: PropTypes.string
};
